"use client";

import React, { useCallback, useEffect, useRef, useState } from "react";

import { cn } from "@perceptron/utils/cn";

const KEYBOARD_STEP = 0.02;

/** A minimum plus the gap the handle sits in, as the second pane's bound is measured from the first. */
export const withGap = (minimum: SplitMinimum, total: number, gap: number): SplitMinimum =>
  `${toPixels(minimum, total) + gap}px`;

export type SplitMinimum = `${number}px` | `${number}%`;

const toPixels = (minimum: SplitMinimum, total: number): number =>
  minimum.endsWith("%") ? (parseFloat(minimum) / 100) * total : parseFloat(minimum);

export const clampFraction = (
  fraction: number,
  total: number,
  minFirst: SplitMinimum,
  minSecond: SplitMinimum,
): number => {
  if (total <= 0) return fraction;

  const first = toPixels(minFirst, total);
  const second = toPixels(minSecond, total);

  // Too narrow for both: the second wins, matching the `min()` in the
  // stylesheet. Holding the first at its minimum crushes the other to a sliver.
  if (first + second >= total) return Math.max(0, 1 - second / total);

  return Math.min(Math.max(fraction, first / total), 1 - second / total);
};

export interface SplitHandleProps {
  orientation: "horizontal" | "vertical";
  /** The element the panes are laid out in. Passed rather than inferred from
   *  the DOM, so an intervening wrapper cannot break it silently. */
  containerRef: React.RefObject<HTMLElement | null>;
  fraction: number;
  onFractionChange: (fraction: number) => void;
  minFirst: SplitMinimum;
  minSecond: SplitMinimum;
  gap: number;
  label: string;
}

/**
 * A sibling of the panes rather than a wrapper: below the breakpoint they are
 * absolutely-positioned sliding tabs, which wrapping would break.
 *
 * The drag writes `--split-fraction` on the container instead of React state.
 * The video timeline, annotation overlay and media grid all recompute from a
 * ResizeObserver, so re-rendering per pointer move puts them on its path.
 */
export const SplitHandle: React.FC<SplitHandleProps> = ({
  orientation,
  containerRef,
  fraction,
  onFractionChange,
  minFirst,
  minSecond,
  gap,
  label,
}) => {
  const isHorizontal = orientation === "horizontal";
  const handleRef = useRef<HTMLDivElement>(null);
  const [isDragging, setIsDragging] = useState(false);

  const applyFraction = useCallback(
    (next: number) => {
      containerRef.current?.style.setProperty("--split-fraction", `${next}`);
      // Kept in step imperatively, because the committed `fraction` does not
      // move until release and the separator would report a stale position for
      // the whole drag.
      handleRef.current?.setAttribute("aria-valuenow", `${Math.round(next * 100)}`);
    },
    [containerRef],
  );

  // The panes transition for the mobile tab slide; left on, the edge would
  // trail the pointer by its duration.
  const setDragging = useCallback(
    (dragging: boolean) => {
      containerRef.current?.setAttribute("data-split-dragging", `${dragging}`);
      setIsDragging(dragging);
    },
    [containerRef],
  );

  // A handle can be unmounted mid-drag — the vertical one goes when the
  // response pane does. Without this the container keeps the attribute and
  // every transition under it stays dead.
  useEffect(() => {
    const container = containerRef.current;
    return () => container?.removeAttribute("data-split-dragging");
  }, [containerRef]);

  useEffect(() => {
    if (!isDragging) applyFraction(fraction);
  }, [fraction, isDragging, applyFraction]);

  const measure = useCallback(() => {
    const container = containerRef.current;
    if (!container) return { start: 0, total: 0 };
    const rect = container.getBoundingClientRect();
    return isHorizontal ? { start: rect.left, total: rect.width } : { start: rect.top, total: rect.height };
  }, [isHorizontal, containerRef]);

  const fractionAt = useCallback(
    (position: number) => {
      const { start, total } = measure();
      if (total <= 0) return fraction;
      // The pane ends half a gap before the pointer; without this it creeps.
      return clampFraction((position - start - gap / 2) / total, total, minFirst, withGap(minSecond, total, gap));
    },
    [measure, gap, minFirst, minSecond, fraction],
  );

  const positionOf = (event: React.PointerEvent) => (isHorizontal ? event.clientX : event.clientY);

  const handlePointerDown = (event: React.PointerEvent<HTMLDivElement>) => {
    // `preventDefault` stops the drag selecting text, but it also suppresses
    // the focus that would normally follow, which would leave the arrow-key
    // nudge unreachable after any click.
    event.preventDefault();
    event.currentTarget.focus();
    event.currentTarget.setPointerCapture(event.pointerId);
    setDragging(true);
  };

  const handlePointerMove = (event: React.PointerEvent<HTMLDivElement>) => {
    if (!isDragging) return;
    // Dragging hands the split from its preferred length to the fraction. `initial`, not removal: the
    // property inherits, and a nested split would pick up its parent's length.
    containerRef.current?.style.setProperty("--split-preferred", "initial");
    applyFraction(fractionAt(positionOf(event)));
  };

  const releaseCapture = (event: React.PointerEvent<HTMLDivElement>) => {
    setDragging(false);
    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }
  };

  const endDrag = (event: React.PointerEvent<HTMLDivElement>) => {
    if (!isDragging) return;
    releaseCapture(event);
    onFractionChange(fractionAt(positionOf(event)));
  };

  // A cancelled drag is one the user did not finish, so it keeps nothing.
  const cancelDrag = (event: React.PointerEvent<HTMLDivElement>) => {
    if (!isDragging) return;
    releaseCapture(event);
    applyFraction(fraction);
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    const back = isHorizontal ? "ArrowLeft" : "ArrowUp";
    const forward = isHorizontal ? "ArrowRight" : "ArrowDown";
    if (event.key !== back && event.key !== forward) return;

    event.preventDefault();
    const step = event.key === back ? -KEYBOARD_STEP : KEYBOARD_STEP;
    const total = measure().total;
    onFractionChange(clampFraction(fraction + step, total, minFirst, withGap(minSecond, total, gap)));
  };

  return (
    <div
      aria-label={label}
      aria-orientation={isHorizontal ? "vertical" : "horizontal"}
      aria-valuemax={100}
      aria-valuemin={0}
      aria-valuenow={Math.round(fraction * 100)}
      className={cn(
        "group/handle z-20 hidden touch-none sm:block",
        "focus-visible:outline-none",
        // Laid over the existing gap; as a flex item it would add a second one.
        "split-handle",
        isHorizontal ? "split-handle-horizontal cursor-col-resize" : "split-handle-vertical cursor-row-resize",
      )}
      data-testid={`split-handle-${orientation}`}
      ref={handleRef}
      role="separator"
      tabIndex={0}
      onKeyDown={handleKeyDown}
      onPointerCancel={cancelDrag}
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={endDrag}
    >
      <span
        aria-hidden="true"
        className={cn(
          "absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 rounded-full bg-foreground-secondary",
          "transition-opacity duration-150",
          isHorizontal ? "h-8 w-1" : "h-1 w-8",
          isDragging ? "opacity-100" : "opacity-0 group-hover/handle:opacity-70 group-focus-visible/handle:opacity-100",
        )}
      />
    </div>
  );
};

/**
 * The values `.split-pane-*` in globals.css reads. The sizing lives there,
 * behind a media query: below the breakpoint an explicit width fights
 * `inset-0` on the absolutely-positioned panes.
 */
export const splitPaneVars = (
  fraction: number,
  minFirst: SplitMinimum,
  minSecond: SplitMinimum,
  gap: number,
  /**
   * A CSS length the first pane aims for instead of `fraction`, clamped by the same minimums. For a default
   * in pixels: the server renders it as is, where a fraction would need the row measured first.
   */
  preferred?: string,
): React.CSSProperties =>
  ({
    // Here as well as in the handle's effect, so the server's markup already
    // has the split and the first paint does not jump.
    "--split-fraction": `${fraction}`,
    // Always set, so a nested split never inherits its parent's preferred length.
    "--split-preferred": preferred ?? "initial",
    "--split-min-first": minFirst,
    "--split-min-second": minSecond,
    "--split-gap": `${gap}px`,
  }) as React.CSSProperties;
